import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../../scss/news.scss";
import { useI18n } from "../../i18n/I18nProvider";
import { MatrixHero } from "../../common/components/MatrixHero";

interface Post {
  id: number;
  title_pl: string;
  desc_pl: string;
  title_eng: string;
  desc_eng: string;
  author: string;
  is_public: boolean;
  created_at: string;
}

interface ApiResponse {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  posts: Post[];
}

const buildPageRange = (current: number, total: number, siblings = 1): (number | "dots")[] => {
  const range: (number | "dots")[] = [];
  const left = Math.max(2, current - siblings);
  const right = Math.min(total - 1, current + siblings);

  range.push(1);
  if (left > 2) range.push("dots");
  for (let i = left; i <= right; i++) range.push(i);
  if (right < total - 1) range.push("dots");
  if (total > 1) range.push(total);

  return range;
};

const NewsComponent: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useI18n();
  const [posts, setPosts] = useState<Post[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);

  const API_URL = `${process.env.REACT_APP_BASE_URL}/v2/main_page/posts/public`;
  const PAGE_SIZE = 12;

  const fetchPosts = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}?page=${page}&page_size=${PAGE_SIZE}&sort_order=desc`
      );

      if (!response.ok) {
        throw new Error(t("news_error_fetch"));
      }

      const data: ApiResponse = await response.json();
      setPosts(data.posts);
      setTotalPages(data.total_pages);
      setCurrentPage(data.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("news_error_generic"));
    } finally {
      setLoading(false);
    }
  }, [API_URL, t]);

  useEffect(() => {
    fetchPosts(1);
  }, [fetchPosts]);

  const goToPage = useCallback((page: number) => {
    if (page < 1 || page > totalPages || page === currentPage || loading) return;
    fetchPosts(page);
    // scroll to the top of the grid after page change
    setTimeout(() => {
      gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [currentPage, totalPages, loading, fetchPosts]);

  const pages = useMemo(() => buildPageRange(currentPage, totalPages), [currentPage, totalPages]);

  const openPost = (postId: number) => {
    navigate(`/aktualnosci/${postId}`);
  };

  const getPostImageUrl = (postId: number): string => {
    return `https://cybernews.wsiz.edu.pl/images/cc_posts/${postId}.png`;
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = "/post_backup.png";
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === "en" ? "en-GB" : "pl-PL", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPreview = (markdown: string, maxLength: number = 150): string => {
    // Usuń markdown formatting dla preview
    const plainText = markdown
      .replace(/#{1,6}\s/g, "")
      .replace(/\*\*/g, "")
      .replace(/\*/g, "")
      // eslint-disable-next-line no-useless-escape
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1")
      // eslint-disable-next-line no-useless-escape
      .replace(/!\[([^\]]*)\]\([^\)]+\)/g, "")
      .replace(/```[\s\S]*?```/g, "")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/\n/g, " ")
      .trim();

    return plainText.length > maxLength
      ? plainText.substring(0, maxLength) + "..."
      : plainText;
  };

  return (
    <section className="news-section">
      <MatrixHero
        title={t("news_page_title")}
        subtitle={t("news_page_subtitle")}
      />
      <div className="news-container">

        {error && (
          <div className="news-error" data-aos="fade-up">
            <p>⚠️ {error}</p>
          </div>
        )}

        <div className="news-grid" ref={gridRef}>
          {posts.map((post, index) => (
            <article
              key={post.id}
              className="news-card"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="news-card-image-wrapper">
                <img
                  src={getPostImageUrl(post.id)}
                  alt={language === "en" && post.title_eng ? post.title_eng : post.title_pl}
                  className="news-card-image"
                  onError={handleImageError}
                />
              </div>
              <div className="news-meta">
                <div className="news-author">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span>{post.author}</span>
                </div>
                <div className="news-date-time">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <time>{formatDate(post.created_at)}</time>
                </div>
              </div>
              <h2 className="news-title">{language === "en" && post.title_eng ? post.title_eng : post.title_pl}</h2>
              
              <div className="news-description">
                <p>{getPreview(language === "en" && post.desc_eng ? post.desc_eng : post.desc_pl)}</p>
              </div>

              <button
                className="news-read-more"
                onClick={() => openPost(post.id)}
              >
                {t("news_read_more")}
              </button>
            </article>
          ))}
        </div>

        {loading && (
          <div className="news-loading" data-aos="fade-up">
            <div className="spinner"></div>
            <p>{t("news_loading")}</p>
          </div>
        )}

        {/* ── PAGINATION ── */}
        {!loading && totalPages > 1 && (
          <nav
            className="news-pagination"
            role="navigation"
            aria-label={t("news_pagination_label")}
          >
            {/* Previous */}
            <button
              className="news-pagination__btn news-pagination__btn--prev"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label={t("news_pagination_prev")}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span className="news-pagination__btn-text">{t("news_pagination_prev")}</span>
            </button>

            {/* Page numbers */}
            <ul className="news-pagination__list">
              {pages.map((p, i) =>
                p === "dots" ? (
                  <li key={`dots-${i}`} className="news-pagination__dots" aria-hidden="true">
                    &hellip;
                  </li>
                ) : (
                  <li key={p}>
                    <button
                      className={`news-pagination__page ${p === currentPage ? "is-active" : ""}`}
                      onClick={() => goToPage(p)}
                      aria-current={p === currentPage ? "page" : undefined}
                      aria-label={`${t("news_pagination_page")} ${p}`}
                    >
                      {p}
                    </button>
                  </li>
                )
              )}
            </ul>

            {/* Next */}
            <button
              className="news-pagination__btn news-pagination__btn--next"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label={t("news_pagination_next")}
            >
              <span className="news-pagination__btn-text">{t("news_pagination_next")}</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </nav>
        )}

        {posts.length === 0 && !loading && !error && (
          <div className="news-empty" data-aos="fade-up">
            <p>{t("news_empty")}</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsComponent;

